"use client";
import { useTranslations } from "@zapier/i18n";

import { useState, useCallback, useMemo } from "react";
import { notFound, useParams } from "next/navigation";
import {
  Alert,
  BetaDataTableProvider as TableProvider,
} from "@zapier/design-system";
import {
  FolderContext,
  FolderTreeItem,
  useFolderActionPermissions,
  useFolderAncestors,
  useQueryFolderDiscovery,
  useFolderTreeFolder,
  useOrganizationZaps,
  useSplitTreatments,
  Zap,
} from "@zapier/client";
import {
  EmptyTableDisplay,
  BulkActionsBarWrapper,
  AccountMismatchModal,
  useEmitFolderActionEvent,
  FolderActionInteractionOption,
  ZapModals,
  useFolderAndZapsModalStates,
  useRouterParams,
  useModalUrlParameters,
  Pagination,
  PageSizeProvider,
  usePageSize,
  FolderModal,
} from "@zapier/shared";
import {
  FolderTypeQueryState,
  getCreateZapUrl,
  isTrashFolder,
  isVirtualFolderNotDefault,
  parseFolderId,
  useNotification,
  FOLDER_ASSETS_PAGE_SIZE,
} from "@zapier/utils";
import { FolderHeader } from "./FolderHeader";
import { FolderZapTable } from "./FolderZapTable";
import { FolderModals } from "../folders/FolderTable/FolderModals";
import { useAppsFromZapsList } from "../zaps/ZapsList/useAppsFromZapsList";
import styles from "./Folder.module.css";

function FolderWithPageSize() {
  const t = useTranslations("folder-page");

  // get folderId from URL
  const parameters = useParams();

  const { page, memberId } = useRouterParams();
  const { size: pageSize } = usePageSize();
  const folderId = parseFolderId(String(parameters?.folder));
  const isTrashFolderRoute = isTrashFolder(folderId);
  const { isZapAndCanvasUnifiedTrashEnabled } = useSplitTreatments();

  const { createNotification } = useNotification();
  const { emit } = useEmitFolderActionEvent();

  const { ancestors } = useFolderAncestors({
    folderId,
  });

  const { canCreateSubfolderLimit, subfolderLimitMessage, isReadOnly } =
    useFolderActionPermissions({
      folderId,
      folderDepth: (ancestors?.length || 0) + 1,
    });

  const {
    folder,
    isLoading: isFoldersLoading,
    totalSubfolderPages,
    paginatedSubfolders,
    isError: folderError,
    errorStatus: folderErrorStatus,
  } = useFolderTreeFolder({ folderId, page, includeChildren: true });

  const {
    zaps,
    isLoading,
    totalPages,
    isError: zapError,
    errorStatus: zapErrorStatus,
    totalResults,
  } = useOrganizationZaps({
    folderId,
    // Assume that the folder.children will have a length of 25 if folder is loading
    subfoldersOffset: isFoldersLoading
      ? FOLDER_ASSETS_PAGE_SIZE
      : folder?.children?.length,
    ...(page ? { page } : {}),
    pageSize,
    ownerId: Number(memberId),
  });

  // Get the apps associated with Zaps for service tooltips in table
  const apps = useAppsFromZapsList({ zaps });

  const { accountInfo, isLoading: isFolderDiscoveryLoading } =
    useQueryFolderDiscovery({
      folderId,
      hasError: folderError || zapError,
    });

  const [selectedZapIds, setSelectedZapIds] = useState<string[] | undefined>();

  // Clear selected zaps
  const clearZapSelection = useCallback(() => {
    setSelectedZapIds(undefined);
  }, [setSelectedZapIds]);

  const {
    approvalType,
    currentFolder,
    currentFolderModal,
    currentZap,
    currentZapModal,
    setApprovalType,
    setCurrentFolder,
    setCurrentFolderModal,
    setCurrentZap,
    setCurrentZapModal,
  } = useFolderAndZapsModalStates();

  // Handle modal URL parameters with orientation delay
  const { closeModalAndClearParameters } = useModalUrlParameters({
    setCurrentFolderModal,
    setCurrentZapModal,
    setCurrentFolder,
    setCurrentZap,
    folder,
    zaps: zaps as Zap[],
    isLoading: isLoading || isFoldersLoading || isFolderDiscoveryLoading,
  });

  // Pass selected zaps to bulk actions component for operations there
  const selectedZaps = useMemo(() => {
    if (!selectedZapIds) {
      return [] as Zap[];
    }
    return zaps.filter((zap) => selectedZapIds.includes(zap.id)) as Zap[];
  }, [selectedZapIds, zaps]);

  // Current Zap is used for the kebab menu actions to render the correct modal
  // Selected Zaps are used for bulk actions to render the correct modal
  const currentZaps = useMemo(() => {
    if (currentZap) {
      return [currentZap];
    }

    if (selectedZaps.length > 0) {
      return selectedZaps;
    }
  }, [selectedZaps, currentZap]);

  if (
    (folderErrorStatus == 403 || zapErrorStatus == 403) &&
    accountInfo?.name &&
    !isFolderDiscoveryLoading &&
    !isFoldersLoading &&
    !isFolderDiscoveryLoading
  ) {
    return (
      <AccountMismatchModal
        account={accountInfo}
        noun="folder"
        continueText={t("actions.continue-to-folders")}
      />
    );
  }

  if (
    (folderErrorStatus == 401 ||
      zapErrorStatus == 401 ||
      zapErrorStatus == 404) &&
    !isLoading &&
    !isFoldersLoading &&
    !isFolderDiscoveryLoading
  ) {
    return notFound();
  }

  return (
    <FolderContext.Provider value={folder}>
      <TableProvider>
        <FolderHeader
          folder={folder as FolderTreeItem}
          folderId={folderId}
          isFoldersLoading={isFoldersLoading}
        />

        {isTrashFolderRoute && isZapAndCanvasUnifiedTrashEnabled && (
          <div className={styles.trashRetentionAlert}>
            <Alert
              variant="banner"
              status="info"
              title={t("alerts.trash-retention")}
            />
          </div>
        )}

        {isReadOnly && (
          <div className={styles.readOnlyFolderAlert}>
            <Alert
              variant="default"
              title={t("alerts.read-only-title")}
              status="warning"
            >
              {t("alerts.plan-limitation-description")}
            </Alert>
          </div>
        )}

        <FolderModals
          currentModal={currentFolderModal}
          setCurrentModal={setCurrentFolderModal}
          currentFolder={currentFolder}
          setCurrentFolder={setCurrentFolder}
          closeModalAndClearParameters={closeModalAndClearParameters}
        />
        <ZapModals
          approvalType={approvalType}
          clearZapSelection={clearZapSelection}
          currentModal={currentZapModal}
          currentZaps={currentZaps as Zap[]}
          setApprovalType={setApprovalType}
          setCurrentModal={setCurrentZapModal}
          setCurrentZap={setCurrentZap}
          closeModalAndClearParameters={closeModalAndClearParameters}
        />

        {!isLoading &&
        !isFoldersLoading &&
        !isFolderDiscoveryLoading &&
        !zaps?.length &&
        !folder?.children?.length ? (
          <>
            <EmptyTableDisplay
              headerText={t("empty-state.no-zaps-message")}
              icon="navFolder"
              actionButtonText={t("buttons.create-subfolder")}
              actionButtonColor="tertiary"
              onActionButton={() => {
                // Cannot a subfolder more than 5 levels deep
                if (canCreateSubfolderLimit) {
                  emit({
                    optionSelected:
                      FolderActionInteractionOption.CreateSubfolder,
                    interactionRawText: folder?.isShared
                      ? FolderTypeQueryState.Shared
                      : FolderTypeQueryState.Personal,
                  });
                  setCurrentFolder(folder);
                  setCurrentFolderModal(FolderModal.Create);
                } else {
                  createNotification({
                    message: subfolderLimitMessage,
                    type: "warning",
                  });
                }
              }}
              secondActionButtonText={t("buttons.create-zap")}
              secondActionButtonIcon="formAdd"
              secondActionButtonHref={getCreateZapUrl(folderId)}
              subHeaderText={t("descriptions.zaps-workflow-explanation")}
            />
          </>
        ) : (
          <>
            <FolderZapTable
              ariaLabel={t("accessibility.folder-table-label", {
                folderName: folder?.name || "",
              })}
              folder={folder}
              folders={paginatedSubfolders || []}
              isLoading={
                isLoading || isFoldersLoading || isFolderDiscoveryLoading
              }
              selectedZapIds={selectedZapIds}
              setApprovalType={setApprovalType}
              setCurrentFolder={setCurrentFolder}
              setCurrentFolderModal={setCurrentFolderModal}
              setCurrentZap={setCurrentZap}
              setCurrentZapModal={setCurrentZapModal}
              setSelectedZapIds={setSelectedZapIds}
              showCheckboxes={!isVirtualFolderNotDefault(folderId)}
              zaps={zaps as Zap[]}
              apps={apps}
            />

            <BulkActionsBarWrapper />

            <Pagination
              page={page}
              // Take the max of either the total zap pages or total subfolder/children pages
              totalPages={Math.max(totalPages, totalSubfolderPages)}
              totalCount={totalResults}
              isLoading={
                isLoading || isFoldersLoading || isFolderDiscoveryLoading
              }
            />
          </>
        )}
      </TableProvider>
    </FolderContext.Provider>
  );
}

export function Folder() {
  return (
    <div>
      <PageSizeProvider>
        <FolderWithPageSize />
      </PageSizeProvider>
    </div>
  );
}
